import { location } from 'cdui-js';
import { goBackByBrowser } from '../../../lib/browser';
import {
  appendDepositAppQuery,
  applyDepositAppQuery,
  leaveToApp,
  snapshotDepositAppQuery,
} from '../deposit/h5-deposit/appBridge';
import { CRS_RESULT_KIND, type CrsResultKind } from './constants';

/** 首屏 sync 外壳 theme / lang / safeArea（对齐入金 applyDepositAppQuery） */
export const syncCrsAppShell = () => {
  applyDepositAppQuery(snapshotDepositAppQuery());
};

/** 站内跳转带上外壳 query（platform / lang / theme / riseFallMode / safeTop / safeBottom） */
export const withCrsQuery = (path: string, extra?: Record<string, string>) => {
  const params = new URLSearchParams();
  if (extra) {
    Object.entries(extra).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
  }
  appendDepositAppQuery(params);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

/** 结果页路径（result=pending|done|refused）；须 routeReplace，避免返回又回到表单 */
export const crsSubmittedPath = (kind: CrsResultKind) =>
  withCrsQuery('/h5/crs/submitted', { result: kind });

/** 表单路径；reapply=1 时跳过 REFUSED→结果页，直接回填编辑 */
export const crsFormPath = (extra?: { reapply?: string }) =>
  withCrsQuery('/h5/crs', extra?.reapply ? { reapply: extra.reapply } : undefined);

/** 读结果页 query；非法值返回 null，由页面再按 latest 兜底 */
export const readCrsResultKindQuery = (): CrsResultKind | null => {
  const fromRouter = (location.query as Record<string, string> | undefined)?.result;
  const fromUrl =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('result')
      : null;
  const raw = String(fromRouter || fromUrl || '')
    .trim()
    .toLowerCase();
  if (
    raw === CRS_RESULT_KIND.PENDING ||
    raw === CRS_RESULT_KIND.DONE ||
    raw === CRS_RESULT_KIND.REFUSED
  ) {
    return raw;
  }
  return null;
};

/** 表单是否带 reapply=1（拒绝结果页「重新申请」） */
export const readCrsReapplyQuery = (): boolean => {
  const fromRouter = (location.query as Record<string, string> | undefined)?.reapply;
  const fromUrl =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('reapply')
      : null;
  const raw = String(fromRouter || fromUrl || '')
    .trim()
    .toLowerCase();
  return raw === '1' || raw === 'true';
};

/**
 * 解析 CRS 拒绝原因：remark 常为
 * `[{"key":"rejectReason","message":"信息不符"}]`
 */
export const parseCrsRejectReason = (remark: unknown): string => {
  const text = String(remark ?? '').trim();
  if (!text) return '';
  try {
    const data = JSON.parse(text) as unknown;
    if (Array.isArray(data)) {
      const hit = data.find(
        (item) => item && typeof item === 'object' && (item as { key?: string }).key === 'rejectReason'
      ) as { message?: string } | undefined;
      const prefer = String(hit?.message ?? '').trim();
      if (prefer) return prefer;
      const joined = data
        .map((item) =>
          item && typeof item === 'object' ? String((item as { message?: string }).message ?? '').trim() : ''
        )
        .filter(Boolean)
        .join('；');
      return joined;
    }
    if (data && typeof data === 'object') {
      const msg = (data as { rejectReason?: { message?: string }; message?: string }).rejectReason
        ?.message;
      if (msg) return String(msg).trim();
      const top = (data as { message?: string }).message;
      if (top) return String(top).trim();
    }
    return '';
  } catch {
    return text;
  }
};

const inAppWebView = () =>
  typeof window !== 'undefined' && !!(window as Window & { ReactNativeWebView?: unknown }).ReactNativeWebView;

/** 完成页「我知道了」/ 浏览器返回：App 嵌套关壳；浏览器后退 */
export const leaveCrs = () => {
  if (inAppWebView()) {
    leaveToApp();
    return;
  }
  goBackByBrowser();
};
